import React, { type ReactNode, useContext } from 'react';
import { useLocation } from 'react-router-dom';

interface CustomQueryParamContext {
  advertiser_id?: string | null;
  client_id?: string | null;
  redirect_uri?: string | null;
  response_type?: string | null;
  scope?: string | null;
  state?: string | null;
  // If enabled, displays the experimental profile edit workflow.
  // This is used for testing purposes.
  showProfileEdit?: boolean | null;
  showShareableExperiment?: boolean | null;
}

const ctx = React.createContext<CustomQueryParamContext>({});

interface Props {
  children: ReactNode;
}

const findFirstMatchingIfPossible = (
  values: string[],
  regex: RegExp,
): string | undefined =>
  values.find((value) => value?.match(regex)) ?? values[0];

export const processQueryParams = (params: URLSearchParams) => {
  // TODO: Workday are being annoying
  const advertiser_id = findFirstMatchingIfPossible(
    params.getAll('advertiser_id'),
    /^(\d+|seekAnz:organization:seek:[a-zA-Z0-9]+)$/,
  );
  const client_id = params.get('client_id');
  const redirect_uri = params.get('redirect_uri');
  const response_type = params.get('response_type');
  const scope = params.get('scope');
  const state = params.get('state');
  const showProfileEdit = params.get('showProfileEdit') === '1'; // useQueryParams used to map BooleanParam 1 = true, 0 = false
  const showShareableExperiment = params.get('showShareableProfile') === '1';

  return {
    advertiser_id,
    client_id,
    redirect_uri,
    response_type,
    scope,
    state,
    showProfileEdit,
    showShareableExperiment,
  };
};

export const CustomQueryParamProvider = ({ children }: Props) => {
  const searchParams = useLocation().search;

  let params = new URLSearchParams(searchParams);

  // This account incorrectly encodes `&` as `&amp;` which isn't parsed properly by URLSearchParams.
  // Sanitise the search params string before parsing to URLSearchParams.
  if (searchParams?.startsWith('?client_id=46736632')) {
    const regex = /&amp;/g;

    /**
     * The signature of `decodeString` contains `undefined`, however `searchParams` should be
     * guaranteed to be a string given our initial `.startsWith` condition.
     */
    params = new URLSearchParams(searchParams.replace(regex, '&'));
  }

  return (
    <ctx.Provider value={processQueryParams(params)}>{children}</ctx.Provider>
  );
};

export const useCustomQueryParams = () => useContext(ctx);
